import {Component, OnDestroy, OnInit} from '@angular/core';
import {CountryISO, TextLabels} from 'ngx-material-intl-tel-input';
import {UntypedFormControl, Validators} from '@angular/forms';
import {EMPTY, Subject, Subscription} from 'rxjs';
import {Apollo} from 'apollo-angular';
import gql from 'graphql-tag';
import {typedFormGroup, TypedFormGroup} from 'ngx-forms-typed';
import {LanguageType, RoleType, UserMutationInput} from '../../../shared/graphql/globalTypes';
import {catchError, exhaustMap, map, take} from 'rxjs/operators';
import {PostUser, PostUserVariables} from './__generated__/PostUser';
import {ToastrService} from 'ngx-toastr';
import {Router} from '@angular/router';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ImageCropComponent} from '../../../shared/modals/image-crop/image-crop.component';
import {notNull} from '../../../shared/utils/utils';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {ErrorService} from '../../../shared/services/error.service';
import {
  labelFrag,
  MultiSelectType,
} from '../../../shared/components/multi-select/multi-select.component';
import {MultiSelectComponentLabelFragment} from '../../../shared/components/multi-select/__generated__/MultiSelectComponentLabelFragment';
import {GetUserCreate} from './__generated__/GetUserCreate';

export interface FileInput {
  base64: string | null;
  filename: string | null;
}

interface FormData {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  form: any;
  photo: FileInput[];
  role: string;
}

type CreateUserData = Required<
  Omit<UserMutationInput, 'clientMutationId' | 'id' | 'role' | 'photoBase64' | 'photoFilename'>
>;

const createUserPostQuery = gql`
  mutation PostUser($input: UserMutationInput!) {
    updateOrCreateUser(input: $input) {
      user {
        email
        firstName
        lastName
        id
        language
        phone
        role
        photo
      }
      errors {
        field
        messages
      }
    }
  }
`;

const userCreateGetQuery = gql`
  query GetUserCreate {
    labels {
      edges {
        node {
          ...MultiSelectComponentLabelFragment
        }
      }
    }
  }
  ${labelFrag}
`;

const phoneTextLabels: TextLabels = {
  mainLabel: '',
  codePlaceholder: 'Code',
  searchPlaceholderLabel: 'Search',
  noEntriesFoundLabel: 'No countries found',
  nationalNumberLabel: '',
  hintLabel: '',
  invalidNumberError: 'Number is not valid',
  requiredError: 'This field is required',
};

@Component({
  selector: 'app-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.scss'],
  standalone: false,
})
export class CreateUserComponent implements OnInit, OnDestroy {
  admin = false;
  CountryISO = CountryISO;
  phoneTextLabels = phoneTextLabels;
  files: FileInput[] = [];
  role: RoleType = RoleType.USER;
  formData$ = new Subject<FormData>();
  LanguageType = LanguageType;
  photoDeleted = true;
  bsModalRef: BsModalRef | null = null;
  base64 = '';
  labels: MultiSelectComponentLabelFragment[] = [];
  RoleType = RoleType;
  MultiSelectType = MultiSelectType;
  private subscriptions: Subscription[] = [];

  form = typedFormGroup({
    firstName: new UntypedFormControl('', Validators.required),
    lastName: new UntypedFormControl('', Validators.required),
    function: new UntypedFormControl(''),
    email: new UntypedFormControl('', [Validators.email, Validators.required]),
    phone: new UntypedFormControl(null),
    language: new UntypedFormControl(LanguageType.ENGLISH, Validators.required),
    labels: new UntypedFormControl([]),
  }) as TypedFormGroup<CreateUserData>;

  constructor(
    private apollo: Apollo,
    private toastr: ToastrService,
    private router: Router,
    private modalService: BsModalService,
    private formHelperService: FormHelperService,
    private errService: ErrorService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.form.controls.phone.valueChanges.pipe(take(1)).subscribe(() => {
        this.form.controls.phone.markAsPristine();
        this.form.controls.phone.markAsUntouched();
      }),
      this.apollo
        .query<GetUserCreate>({
          query: userCreateGetQuery,
        })
        .pipe(
          catchError(error => {
            this.errService.handleDefault(error);
            return EMPTY;
          }),
        )
        .subscribe(data => {
          this.labels = (data.data?.labels.edges ?? []).map(edge => edge.node);
        }),
      this.formData$
        .pipe(
          map((data: FormData) => {
            return {
              email: data.form.email,
              firstName: data.form.firstName,
              function: data.form.function,
              language: data.form.language,
              lastName: data.form.lastName,
              phone: this.getPhoneNumber(data.form.phone),
              role: this.role,
              photoFilename: this.files[0]?.filename,
              photoBase64: this.files[0]?.base64,
              labels: data.form.labels.map((label: MultiSelectComponentLabelFragment) => label.id),
            } as Required<UserMutationInput>;
          }),
          exhaustMap(data => {
            return this.apollo
              .mutate<PostUser, PostUserVariables>({
                mutation: createUserPostQuery,
                variables: {
                  input: data,
                },
                update: cache => cache.evict({id: 'ROOT_QUERY', fieldName: 'users'}),
              })
              .pipe(
                catchError(error => {
                  this.errService.handleDefault(error);
                  return EMPTY;
                }),
              );
          }),
        )
        .subscribe(data => {
          if (notNull(data.data).updateOrCreateUser.errors.length > 0) {
            this.formHelperService.handleServerErrors(
              this.form,
              notNull(data.data).updateOrCreateUser.errors,
            );
            this.toastr.error($localize`Something went wrong`);
          } else {
            this.toastr.success($localize`User created`);
            this.router.navigate(['users']);
          }
        }),
    );
  }

  openModal(): void {
    this.bsModalRef = this.modalService.show(ImageCropComponent);
    this.bsModalRef.content.base64 = this.base64;
    this.bsModalRef.content.callback = (base64: string) => {
      if (base64 !== '') {
        this.files[0].base64 = base64;
      } else {
        this.photoDeleted = true;
        this.files = [{base64: null, filename: null}];
      }
    };
  }

  onSubmit(): void {
    if (this.admin) {
      this.role = RoleType.ADMIN;
    } else {
      this.role = RoleType.USER;
    }

    if (this.form.valid) {
      this.formData$.next({
        form: this.form.value,
        photo: this.files,
        role: this.role,
      });
      return;
    }
    this.toastr.error($localize`Fill in all fields correctly`);
  }

  changeLanguage(language: string): void {
    if (language === 'English') {
      this.form.controls.language.setValue(LanguageType.ENGLISH);
    } else {
      this.form.controls.language.setValue(LanguageType.DUTCH);
    }
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  imageUpload(event: any): void {
    this.photoDeleted = false;
    const files = event.target.files;
    this.files = [];
    for (let i = 0; i !== files.length; i++) {
      const file = files[i];
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        () => {
          this.files.push({
            base64: reader.result as string,
            filename: file.name,
          });
          this.base64 = reader.result as string;
          event.target.value = '';
          this.openModal();
        },
        false,
      );
      reader.readAsDataURL(files[i]);
    }
  }

  toggleAdmin(): void {
    this.admin = !this.admin;
  }

  private getPhoneNumber(phone: unknown): string {
    if (typeof phone !== 'string' || phone.trim() === '') {
      return '';
    }

    return phone;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }
}
